import React from 'react';
import { Sparkles, Droplets, Scissors, Zap, Syringe, FlaskConical, ShieldCheck, ScanLine } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';

const serviceData = [
  { icon: Sparkles, title: "Acne Treatment", titleHi: "मुहांसे का उपचार", desc: "Best pimple treatment in Jhansi. Advanced protocols to clear persistent acne and prevent future breakouts for lasting clear skin.", color: "from-rose-50 to-pink-50", accent: "#F43F5E" },
  { icon: Droplets, title: "Pigmentation", titleHi: "पिगमेंटेशन", desc: "Expert pigmentation treatment in Jhansi. Targeted therapies to reduce dark spots, melasma, and uneven skin tone for a radiant complexion.", color: "from-amber-50 to-orange-50", accent: "#D97706" },
  { icon: Scissors, title: "Hair Transplant", titleHi: "हेयर ट्रांसप्लांट", desc: "Best hair transplant clinic in Jhansi. Natural-looking hair restoration using latest FUE techniques with minimal downtime.", color: "from-emerald-50 to-teal-50", accent: "#059669" },
  { icon: Zap, title: "Laser Treatment", titleHi: "लेज़र उपचार", desc: "Advanced laser treatment in Jhansi for hair removal, skin rejuvenation, and scar reduction by certified dermatologist.", color: "from-sky-50 to-blue-50", accent: "#0284C7" },
  { icon: Syringe, title: "Botox", titleHi: "बोटोक्स", desc: "Safe and precise anti-aging botox injections in Jhansi to smooth wrinkles and restore youthful appearance.", color: "from-violet-50 to-purple-50", accent: "#7C3AED" },
  { icon: FlaskConical, title: "Chemical Peeling", titleHi: "केमिकल पीलिंग", desc: "Professional-grade chemical peeling in Jhansi to renew skin texture, reduce fine lines, and boost natural glow.", color: "from-lime-50 to-green-50", accent: "#65A30D" },
  { icon: ShieldCheck, title: "Skin Diseases", titleHi: "त्वचा रोग", desc: "Expert skin doctor in Jhansi for eczema, psoriasis, vitiligo, and other skin conditions near Medical College.", color: "from-slate-50 to-gray-50", accent: "#475569" },
  { icon: ScanLine, title: "Scar Treatment", titleHi: "निशान उपचार", desc: "Advanced scar treatment in Jhansi — multi-modal approach to minimize acne scars, surgical scars, and stretch marks.", color: "from-orange-50 to-red-50", accent: "#EA580C" },
];

function ServiceCard({ service, language, t, index }) {
  const [ref, isVisible] = useScrollReveal(0.1);

  const handleBookAppointment = () => {
    window.dispatchEvent(new CustomEvent('selectTreatment', { detail: service.title }));
    document.getElementById('appointment')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      ref={ref}
      className={`group bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-50 hover:-translate-y-2 hover:shadow-[0_16px_50px_rgb(0,0,0,0.1)] transition-all duration-500 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
      data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
        <service.icon className="w-5 h-5" style={{ color: service.accent }} />
      </div>
      <h3 className="font-outfit font-medium text-lg text-[#0F172A] mb-2">
        {language === 'hi' ? service.titleHi : service.title}
      </h3>
      <p className="text-sm text-[#475569] font-manrope leading-relaxed mb-4">
        {service.desc}
      </p>
      <button
        onClick={handleBookAppointment}
        className="text-sm font-manrope font-medium bg-gradient-to-r from-[#D1A760] to-[#B89255] text-white px-4 py-2 rounded-full hover:shadow-[0_4px_16px_rgba(209,167,96,0.4)] transition-all duration-300 flex items-center gap-1.5"
        data-testid={`service-cta-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {t('services_cta')} <span className="transition-all duration-200">&rarr;</span>
      </button>
    </div>
  );
}

export default function ServicesSection() {
  const { language, t } = useLanguage();
  const [headerRef, headerVisible] = useScrollReveal(0.2);

  return (
    <section id="services" className="py-20 md:py-32 bg-[#FAFAFA]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-sm font-manrope font-medium text-[#D1A760] tracking-wide uppercase mb-3">Treatments</p>
          <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight" data-testid="services-title">
            {t('services_title')}
          </h2>
          <p className="mt-4 text-base text-[#475569] font-manrope max-w-xl mx-auto">
            {t('services_subtitle')}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="services-grid">
          {serviceData.map((service, idx) => (
            <ServiceCard key={idx} service={service} language={language} t={t} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
